import {
  Tag as ChakraTag,
  type TagProps as ChakraTagProps,
} from '@chakra-ui/react-v2';
import { forwardRef } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';
import { OverrideProperties } from 'type-fest';

import { useTagGroup } from './TagGroup';
import { TagSize } from './types';

export type TagProps = OverrideProperties<
  ChakraTagProps,
  {
    size?: TagSize;
  }
>;

/**
 * Tags are used to represent an entity or concept.
 * Tags always have a primary action, typically "expand", as in a link or popover.
 * They may also have one secondary action, such as a delete button.
 *
 * The Tag component is designed to be used within a TagGroup to ensure consistent sizing, but it can also be used standalone with direct size specification.
 *
 * Tags only support 'xs' and 'sm' sizes.
 * @see {@link http://storybook.paperlessparts.com/?path=/docs/data-display-tag--docs | Docs}
 */
export const Tag = forwardRef<TagProps, 'span'>(
  ({ size, className, ...restProps }, ref) => {
    const tagGroup = useTagGroup();

    return (
      <ChakraTag
        className={cx('tag', className)}
        size={size ?? tagGroup?.size}
        {...restProps}
        ref={ref}
      />
    );
  },
);

Tag.displayName = 'Tag';
